import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { Col, Row } from 'react-bootstrap';
import CardPieChart from '@/app/common/components/CardPieChart';
import OverviewCard from '@/app/common/components/OverviewCard';
import CardStatus from '../components/history/SiteHealthCard';
import {
  useGetCrawlInfoQuery,
  useGetCrawlStatusQuery,
  useGetDepthStatsQuery,
  useGetNumOfAltsQuery,
  useGetNumOfLinksQuery,
  useGetNumOfErrorWarningInfoesQuery,
  useGetPageSourceInfoQuery
} from '@/app/Crawl/api/overviewApi';
import { useOutletContext, useParams } from 'react-router-dom';
import NoCrawlData from '@/app/Crawl/components/common/NoCrawlData';
import timeStampToDate from '@/helpers/timeStampToDate';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { crawlTooltip } from '../tooltip/crawlTooltips';
import {
  OverviewCardSkeleton,
  OverviewHealthCardSkeleton,
  OverviewPieCardSkeleton
} from '../components/common/OverviewCardSkeleton';
import useDomain from '@/hooks/useDomain';
import { ToolAccessDenied } from '@/app/common/components/ToolAccessGuard';
import useToolAccess from '@/app/common/hooks/useToolAccess';
// صفحه نمای کلی خزش انجام گرفته
type BreadcrumbItem = {
  text: string;
  href: string;
};

type CrawlOutletContext = [
  BreadcrumbItem[],
  Dispatch<SetStateAction<BreadcrumbItem[]>>
];

type OverviewResponse = {
  data?: Record<string, any>;
};

const Overview = () => {
  const [, setBreadcrumbData] = useOutletContext<CrawlOutletContext>();
  useEffect(() => {
    setBreadcrumbData([{ text: 'نمای کلی', href: '#' }]);
  }, []);

  const { slug } = useParams();
  const { domain_slug } = useDomain();
  const access = useToolAccess('crawl');
  const [status, setStatus] = useState({ finished: true, processing: true });
  useGetUserInfoQuery();
  const job_page = slug === 'last' ? domain_slug : slug;

  //گرفتن آخرین وضعیت خزش => بر اساس آن مشخص میشه چه کویری هایی اجرا شود
  const { data: rawCrawlStatus, isLoading } = useGetCrawlStatusQuery(
    { job_page, domain_slug },
    {
      skip:
        access.isLoading ||
        !access.haveAccess ||
        !job_page ||
        !domain_slug
    }
  );
  const crawlStatus = rawCrawlStatus as OverviewResponse | undefined;

  const resolvedJobPage = crawlStatus?.data?.job_page;
  const { data: rawCrawlInfo, isLoading: crawlInfoLoading } =
    useGetCrawlInfoQuery(
      { job_page: resolvedJobPage, domain_slug },
      {
        skip:
          (status.finished && status.processing) ||
          !resolvedJobPage ||
          !domain_slug
      }
    );
  const crawlInfo = rawCrawlInfo as OverviewResponse | undefined;

  const { data: rawCrawlAlt, isLoading: crawlAltLoading } =
    useGetNumOfAltsQuery(crawlStatus?.data?.job_page, {
      skip: status.processing
    });
  const crawlAlt = rawCrawlAlt as OverviewResponse | undefined;

  const {
    data: rawNumOfErrorsWarningInfoes,
    isLoading: errorsWarningInfoesLoading
  } = useGetNumOfErrorWarningInfoesQuery(crawlStatus?.data?.job_page, {
    skip: status.processing
  });
  const numOfErrorsWarningInfoes =
    rawNumOfErrorsWarningInfoes as OverviewResponse | undefined;

  const { data: rawCrawlLinks, isLoading: crawlLinksLoading } =
    useGetNumOfLinksQuery(crawlStatus?.data?.job_page, {
      skip: status.processing
    });
  const crawlLinks = rawCrawlLinks as OverviewResponse | undefined;

  const { data: rawCrawlSource, isLoading: crawlSourceLoading } =
    useGetPageSourceInfoQuery(crawlStatus?.data?.job_page, {
      skip: status.processing
    });
  const crawlSource = rawCrawlSource as OverviewResponse | undefined;

  const { data: rawCrawlDepth, isLoading: crawlDepthLoading } =
    useGetDepthStatsQuery(crawlStatus?.data?.job_page, {
      skip: status.processing
    });
  const crawlDepth = rawCrawlDepth as OverviewResponse | undefined;
  // مشخص میکنیم که موارد دریافت شدن یا نه
  useEffect(() => {
    if (!isLoading && crawlStatus) {
      {
        if (crawlStatus?.data?.completed) {
          setStatus(prevStatus => ({
            ...prevStatus,
            finished: false
          }));
        } else {
          setStatus(prevStatus => ({
            ...prevStatus,
            processing: false
          }));
        }
      }
    }
  }, [isLoading]);

  if (access.isLoading) return <></>;
  if (!access.haveAccess) {
    return <ToolAccessDenied access={access} tool_label="ابزار خزش" />;
  }

  if (!isLoading && !crawlStatus?.data) return <NoCrawlData />;

  return (
    <div className="overflow-hidden">
      <Row className="g-3 mb-3">
        <Col md={6} xl={4}>
          {/* کارت سلامت سایت */}

          {!isLoading && !crawlInfoLoading && crawlInfo ? (
            <CardStatus
              title="سلامت سایت"
              tooltip={crawlTooltip.crawlerOverview.cardStatus}
              data={crawlInfo?.data?.health_score}
              crawlStatus={
                crawlStatus?.data as {
                  completed: boolean;
                  status?: number | null;
                }
              }
            />
          ) : (
            <OverviewHealthCardSkeleton
              title="سلامت سایت"
              tooltip={crawlTooltip.crawlerOverview.cardStatus}
            />
          )}
        </Col>
        <Col md={6} xl={4}>
          {/* کارت اطلاعات خزش */}
          {!crawlInfoLoading && crawlInfo ? (
            <OverviewCard
              title="اطلاعات خزش"
              tooltip={crawlTooltip.crawlerOverview.cardInformation}
              data={[
                // تبیل داده ها به شکل قابل درک کامپوننت
                {
                  id: 1,
                  value: crawlInfo?.data?.num_of_urls,
                  name: 'آدرس خزش شده',
                  color: 'primary'
                },
                {
                  id: 2,
                  value: crawlInfo?.data?.num_of_pages,
                  name: 'صفحات خزش شده',
                  color: 'warning'
                },
                {
                  id: 3,
                  value: timeStampToDate(
                    crawlInfo?.data?.start_crawl_time as string
                  ),
                  name: 'آخرین خزش',
                  color: 'secondary'
                },
                {
                  id: 4,
                  value: `${crawlInfo?.data?.crawl_duration ? crawlInfo.data.crawl_duration + ' دقیقه' : 'درحال خزش'} `,
                  name: 'مدت زمان خزش',
                  color: 'info'
                }
              ]}
            />
          ) : (
            <OverviewCardSkeleton
              title="اطلاعات خزش"
              tooltip={crawlTooltip.crawlerOverview.cardInformation}
              items={4}
            />
          )}
        </Col>
        <Col md={6} xl={4}>
          {/* کارت فهرست وضعیت */}
          {!errorsWarningInfoesLoading &&
          !crawlInfoLoading &&
          (numOfErrorsWarningInfoes || crawlInfo) ? (
            <OverviewCard
              title="فهرست وضعیت"
              tooltip={crawlTooltip.crawlerOverview.cardChecklist}
              data={[
                {
                  // تبیل داده ها به شکل قابل درک کامپوننت
                  id: 1,
                  value: numOfErrorsWarningInfoes
                    ? numOfErrorsWarningInfoes?.data?.num_of_errors
                    : crawlInfo?.data?.num_of_errors,
                  name: 'خطا',
                  color: 'danger'
                },
                {
                  id: 2,
                  value: numOfErrorsWarningInfoes
                    ? numOfErrorsWarningInfoes?.data?.num_of_warnings
                    : crawlInfo?.data?.num_of_warnings,
                  name: 'هشدار',
                  color: 'warning'
                },
                {
                  id: 3,
                  value: numOfErrorsWarningInfoes
                    ? numOfErrorsWarningInfoes?.data?.num_of_infoes
                    : crawlInfo?.data?.num_of_infoes,
                  name: 'اطلاعیه',
                  color: 'info'
                }
              ]}
            />
          ) : (
            <OverviewCardSkeleton
              title="فهرست وضعیت"
              tooltip={crawlTooltip.crawlerOverview.cardChecklist}
              items={3}
            />
          )}
        </Col>
        <Col md={6} xl={4}>
          {/* کارت کد های وضعیت خزش */}
          {!crawlInfoLoading &&
          !crawlSourceLoading &&
          (crawlInfo || crawlSource) ? (
            (crawlInfo || crawlSource) && (
              <CardPieChart
                title="توزیع کد های وضعیت"
                tooltip={crawlTooltip.crawlerOverview.cardCodeDistribution}
                data={[
                  // تبیل داده ها به شکل قابل درک کامپوننت
                  {
                    id: 1,
                    value: crawlSource
                      ? crawlSource?.data?.num_of_2xx_status_code
                      : crawlInfo?.data?.num_of_2xx_status_code,
                    name: 'Success (2xx)',
                    color: 'success'
                  },
                  {
                    id: 2,
                    value: crawlSource
                      ? crawlSource?.data?.num_of_3xx_status_code
                      : crawlInfo?.data?.num_of_3xx_status_code,
                    name: 'Redirect (3xx)',
                    color: 'info'
                  },
                  {
                    id: 3,
                    value: crawlSource
                      ? crawlSource?.data?.num_of_4xx_status_code
                      : crawlInfo?.data?.num_of_4xx_status_code,
                    name: 'Client error (4xx)',
                    color: 'warning'
                  },
                  {
                    id: 4,
                    value: crawlSource
                      ? crawlSource?.data?.num_of_5xx_status_code
                      : crawlInfo?.data?.num_of_5xx_status_code,
                    name: 'Server error (5xx)',
                    color: 'danger'
                  }
                ]}
                radius={['100%', '77%']}
              />
            )
          ) : (
            <OverviewPieCardSkeleton
              title="توزیع کد های وضعیت"
              tooltip={crawlTooltip.crawlerOverview.cardCodeDistribution}
              items={4}
            />
          )}
        </Col>
        <Col md={6} xl={4}>
          {/* کارت پروتکل ها */}
          {!crawlInfoLoading &&
          !crawlSourceLoading &&
          (crawlInfo || crawlSource) ? (
            (crawlInfo || crawlSource) && (
              <CardPieChart
                title="توزیع پروتکل ها"
                tooltip={crawlTooltip.crawlerOverview.cardProtocolDistribution}
                data={[
                  // تبیل داده ها به شکل قابل درک کامپوننت
                  {
                    id: 1,
                    value: crawlSource
                      ? crawlSource?.data?.num_of_https
                      : crawlInfo?.data?.num_of_https,
                    name: 'HTTPS',
                    color: 'success'
                  },
                  {
                    id: 2,
                    value: crawlSource
                      ? crawlSource?.data?.num_of_http
                      : crawlInfo?.data?.num_of_http,
                    name: 'HTTP',
                    color: 'danger'
                  }
                ]}
                radius={['100%', '77%']}
              />
            )
          ) : (
            <OverviewPieCardSkeleton
              title="توزیع پروتکل ها"
              tooltip={crawlTooltip.crawlerOverview.cardProtocolDistribution}
              items={2}
            />
          )}
        </Col>
        <Col md={6} xl={4}>
          {/* کارت تصاویر دارا و بدون alt */}
          {!crawlInfoLoading && !crawlAltLoading && (crawlAlt || crawlInfo) ? (
            (crawlAlt || crawlInfo) && (
              <CardPieChart
                tooltip={crawlTooltip.crawlerOverview.cardAlt}
                title="تصاویر با / بدون alt"
                data={[
                  // تبیل داده ها به شکل قابل درک کامپوننت
                  {
                    id: 1,
                    value: crawlAlt
                      ? crawlAlt?.data?.num_of_with_alt
                      : crawlInfo?.data?.num_of_with_alt,
                    name: 'دارای alt',
                    color: 'success'
                  },
                  {
                    id: 2,
                    value: crawlAlt
                      ? crawlAlt?.data?.num_of_without
                      : crawlInfo?.data?.num_of_without,
                    name: 'بدون alt',
                    color: 'danger'
                  }
                ]}
                radius={['100%', '77%']}
              />
            )
          ) : (
            <OverviewPieCardSkeleton
              title="تصاویر با / بدون alt"
              tooltip={crawlTooltip.crawlerOverview.cardAlt}
              items={2}
            />
          )}
        </Col>
        <Col md={6} xl={4}>
          {/* کارت توزیع آدرس های خزش شده */}
          {!crawlInfoLoading &&
          !crawlLinksLoading &&
          (crawlLinks || crawlInfo) ? (
            (crawlLinks || crawlInfo) && (
              <CardPieChart
                title="توزیع آدرس های خزش شده"
                tooltip={crawlTooltip.crawlerOverview.cardLinkDistribution}
                data={[
                  // تبیل داده ها به شکل قابل درک کامپوننت
                  {
                    id: 1,
                    value: crawlLinks
                      ? crawlLinks?.data?.num_of_internal
                      : crawlInfo?.data?.num_of_internal,
                    name: 'پیوند های داخلی',
                    color: 'ourcolor-1'
                  },
                  {
                    id: 2,
                    value: crawlLinks
                      ? crawlLinks?.data?.num_of_external
                      : crawlInfo?.data?.num_of_external,
                    name: 'پیوند های خارجی',
                    color: 'ourcolor-2'
                  }
                ]}
                radius={['100%', '77%']}
              />
            )
          ) : (
            <OverviewPieCardSkeleton
              title="توزیع آدرس های خزش شده"
              tooltip={crawlTooltip.crawlerOverview.cardLinkDistribution}
              items={2}
            />
          )}
        </Col>
        <Col md={6} xl={4}>
          {/* کارت توزیع انوا محتوا */}
          {!crawlInfoLoading &&
          !crawlSourceLoading &&
          (crawlSource || crawlInfo) ? (
            (crawlSource || crawlInfo) && (
              <CardPieChart
                title="توزیع نوع محتوا"
                tooltip={crawlTooltip.crawlerOverview.cardContentDistribution}
                data={[
                  // تبیل داده ها به شکل قابل درک کامپوننت
                  {
                    id: 1,
                    value: crawlSource
                      ? crawlSource?.data?.num_of_pages
                      : crawlInfo?.data?.num_of_pages,
                    name: 'صفحات',
                    color: 'primary'
                  },
                  {
                    id: 2,
                    value: crawlSource
                      ? crawlSource?.data?.num_of_images
                      : crawlInfo?.data?.num_of_images,
                    name: 'تصاویر',
                    color: 'success'
                  },
                  {
                    id: 3,
                    value: crawlSource
                      ? crawlSource?.data?.num_of_css
                      : crawlInfo?.data?.num_of_css,
                    name: 'فایل های css',
                    color: 'info'
                  },
                  {
                    id: 4,
                    value: crawlSource
                      ? crawlSource?.data?.num_of_js
                      : crawlInfo?.data?.num_of_js,
                    name: 'فایل های javascript',
                    color: 'warning'
                  }
                ]}
                radius={['100%', '77%']}
              />
            )
          ) : (
            <OverviewPieCardSkeleton
              title="توزیع نوع محتوا"
              tooltip={crawlTooltip.crawlerOverview.cardContentDistribution}
              items={4}
            />
          )}
        </Col>
        <Col md={6} xl={4}>
          {/* کارت توزیع عمق خزش */}
          {!crawlInfoLoading &&
          !crawlDepthLoading &&
          (crawlInfo?.data || crawlDepth?.data) ? (
            (crawlInfo?.data || crawlDepth?.data) && (
              <CardPieChart
                title="توزیع عمق خزش"
                tooltip={crawlTooltip.crawlerOverview.cardDepthDistribution}
                type="crawlDepth"
                data={
                  // تبیل داده ها به شکل قابل درک کامپوننت
                  crawlDepth
                    ? crawlDepth?.data
                    : crawlInfo?.data?.num_of_page_depth
                }
                radius={['100%', '77%']}
              />
            )
          ) : (
            <OverviewPieCardSkeleton
              title="توزیع عمق خزش"
              tooltip={crawlTooltip.crawlerOverview.cardDepthDistribution}
              items={4}
            />
          )}
        </Col>
      </Row>
    </div>
  );
};

export default Overview;
